import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import normalizePhone from 'utils/normalizePhone';

/**
 * "Viajar como Maria G." — filling a passenger with the Gontijo account the buyer signed in with.
 *
 * Ported from saas-app (`components/Passengers/passenger/util/accountDocument.ts`,
 * `accountShortName.ts` and `travelAsAccount` in `passenger/index.js`), where the rules were settled
 * against the carrier's real records. Kept as pure functions so the rules can be read, and tested,
 * without a form around them.
 */

/** The types a Brazilian passenger's document can be held under. */
const DOCUMENT_TYPES = ['CPF', 'RG', 'PAS'];

/** Patterns a number must match before it is written, one per type. Same as saas-app's. */
const DOCUMENT_PATTERNS = {
  CPF: /^\d{11}$/,
  RG: /^\d{7,14}$/,
  PAS: /^[A-Z0-9]{6,12}$/i,
};

/** A Brazilian mobile with its area code: 10 or 11 digits. */
const BRAZILIAN_PHONE = /^\d{10,11}$/;

/** Good enough to offer, not a validation: the email field runs its own. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The account's birth date as the birth date field keeps it, `YYYY-MM-DD`, or undefined.
 *
 * The ERP sends `dataNascClie` as compact `YYYYMMDD`, though some of its records carry it dashed;
 * both are read. A date that does not exist on the calendar is left out rather than written.
 * @param {string} [birthDate] - The account's birth date.
 * @returns {string|undefined} The date to write, or undefined.
 */
export function accountBirthDate(birthDate) {
  const match = (birthDate ?? '').trim().match(/^(\d{4})-?(\d{2})-?(\d{2})$/);
  if (!match) return undefined;

  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const isOnCalendar =
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day);

  return isOnCalendar ? `${year}-${month}-${day}` : undefined;
}

/**
 * "Maria Guadalupe Ortiz Mendez" → "Maria G." — the name the pill wears.
 *
 * A first name and an initial is what the buyer recognises as their own account, and it leaves room
 * beside a heading that already carries the passenger number and the seats.
 * @param {string} fullName - The account holder's full name.
 * @returns {string} The short name, or '' when there is no name — the pill's cue not to render.
 */
export function accountShortName(fullName) {
  const [firstName, ...rest] = (fullName ?? '').trim().split(/\s+/).filter(Boolean);

  if (!firstName) return '';
  if (rest.length === 0) return firstName;

  return `${firstName} ${rest[0].charAt(0).toUpperCase()}.`;
}

/**
 * The account's name as this form's two fields: the first word, and everything after it.
 * @param {string} fullName - The account holder's full name.
 * @returns {{firstName: string, lastName: string}} The split name; either half may be ''.
 */
function splitAccountName(fullName) {
  const [firstName = '', ...rest] = (fullName ?? '').trim().split(/\s+/).filter(Boolean);

  return { firstName, lastName: rest.join(' ') };
}

/** Whether a number holds up as a document of the given type. */
function isValidDocument(documentNumber, documentType) {
  const pattern = DOCUMENT_PATTERNS[documentType];
  if (!pattern || !pattern.test(documentNumber)) return false;

  return documentType !== 'CPF' || cpfValidator.isValid(documentNumber);
}

/** The digits of a CPF, or the number as-is for the other types. */
function cleanDocumentNumber(documentNumber, documentType) {
  const trimmed = (documentNumber ?? '').trim();

  return documentType === 'CPF' ? trimmed.replace(/\D/g, '') : trimmed;
}

/**
 * What the account's document actually is, or undefined when that cannot be established.
 *
 * The carrier's own description (`dscrTipoDocmClie`) wins when it arrives. A description this form
 * has no type for counts as unknown rather than being guessed at. Without one, the number can only
 * ever conclude CPF, and only if it validates as one: that is a check, not an assumption, and it is
 * what keeps the pill useful against a backend that does not forward the type yet.
 * @param {{documentType?: string, documentNumber: string}} account - The account record.
 * @returns {string|undefined} 'CPF', 'RG', 'PAS', or undefined.
 */
function documentTypeOf({ documentType, documentNumber }) {
  const declared = documentType?.trim().toUpperCase();

  if (declared) return DOCUMENT_TYPES.includes(declared) ? declared : undefined;

  return isValidDocument(cleanDocumentNumber(documentNumber, 'CPF'), 'CPF') ? 'CPF' : undefined;
}

/**
 * The number the pill writes into the passenger's document field, or undefined to leave the field —
 * and the type picker — exactly as they are.
 *
 * The account holds one document and the form declares a type: a CPF written into a form set to RG
 * would leave with the ticket declaring it an RG. So the number is written only when the two agree,
 * and only once it holds up as that type — for a CPF, its check digits too. A CPF travels as bare
 * digits, which is how this form keeps them.
 * @param {{documentType?: string, documentNumber: string}} account - The account record.
 * @param {string} [selectedType] - The type the passenger form is set to.
 * @returns {string|undefined} The number to write, or undefined.
 */
export function accountDocumentNumber(account, selectedType) {
  const documentType = documentTypeOf(account);
  if (!documentType || documentType !== selectedType) return undefined;

  const documentNumber = cleanDocumentNumber(account.documentNumber, documentType);

  return isValidDocument(documentNumber, documentType) ? documentNumber : undefined;
}

/**
 * Every field the pill writes into one passenger, keyed by the form's own field names.
 *
 * A value the account does not hold is left out rather than written blank, so the offer never wipes
 * something the buyer already typed. The document type is never among them: whoever is on RG stays
 * on RG and types their own number.
 * @param {object} params - Inputs.
 * @param {{name: string, documentNumber: string, documentType?: string, phone?: string, email?: string, birthDate?: string}} params.account - The carrier's record.
 * @param {?string} [params.email] - The address the buyer signed in with. The carrier's own address
 *   wins when the record carries one: the widget announces this one only at sign-in, so a session
 *   restored from storage may not hold it.
 * @param {string} [params.selectedDocumentType] - The type the passenger form is set to.
 * @returns {object} Field name → value.
 */
export function buildTravelAsAccountPatch({ account, email, selectedDocumentType }) {
  if (!account) return {};

  const { firstName, lastName } = splitAccountName(account.name);
  const documentId = accountDocumentNumber(account, selectedDocumentType);
  const phoneDigits = (account.phone ?? '').replace(/\D/g, '');
  const accountEmail = (account.email || email || '').trim();
  const dateOfBirth = accountBirthDate(account.birthDate);

  return {
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(documentId && { documentId }),
    // Formatted the way the phone field formats a keystroke, with the country the number belongs
    // to: the carrier stores Brazilian mobiles, area code included.
    ...(BRAZILIAN_PHONE.test(phoneDigits) && {
      phoneCountry: 'BR',
      phoneCode: '55',
      phone: normalizePhone(phoneDigits, 'BR'),
    }),
    ...(EMAIL_SHAPE.test(accountEmail) && { email: accountEmail }),
    ...(dateOfBirth && { dateOfBirth }),
  };
}

/** The phone's country and dialling code: the form's defaults, not anything personal to clear. */
const KEPT_WHEN_CLEARING = ['phoneCountry', 'phoneCode'];

/** A passenger's full name, compared the way the duplicate-name check compares it. */
function comparableName(firstName, lastName) {
  return `${firstName ?? ''}${lastName ?? ''}`.replace(/\s+/g, '').toLowerCase();
}

/**
 * The fields to blank on another passenger that still holds the account, so moving the account
 * from one passenger to another leaves no duplicate behind.
 *
 * An account travels once, so a passenger whose name or document is still the account's is the
 * earlier tap. The name is cleared while it would still count as a duplicate name; every other
 * field only while it still holds the patch's value, so anything the buyer typed over stays.
 * @param {object} [passengerValues] - The other passenger's current form values.
 * @param {object} patch - What `buildTravelAsAccountPatch` wrote, or is about to write.
 * @returns {string[]} The field names to blank; empty when that passenger is not the account.
 */
export function accountFieldsHeldBy(passengerValues, patch) {
  if (!passengerValues) return [];

  const patchName = comparableName(patch.firstName, patch.lastName);
  const holdsName =
    Boolean(patchName) &&
    comparableName(passengerValues.firstName, passengerValues.lastName) === patchName;
  const holdsDocument =
    Boolean(patch.documentId) && passengerValues.documentId === patch.documentId;
  if (!holdsName && !holdsDocument) return [];

  return Object.keys(patch).filter((field) => {
    if (KEPT_WHEN_CLEARING.includes(field)) return false;
    // The name goes as a pair whenever the duplicate check would still catch it.
    if (field === 'firstName' || field === 'lastName') return holdsName;

    return passengerValues[field] === patch[field];
  });
}
